import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Menu,
  MenuItem,
  useTheme,
} from '@mui/material';
import {
  Login as LoginIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authPath } from '../utils/authRedirect';
import { trackCtaClick } from '../analytics';

const PublicNavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { isAuthenticated, user } = useAuth();
  const loginHref = authPath('/login', location.search);
  const registerHref = authPath('/register', location.search);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);

  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleNavigation = (path, ctaLocation = 'public_nav_mobile') => {
    if (path === registerHref) {
      trackCtaClick('register', ctaLocation);
    } else if (path === '/install-gateway') {
      trackCtaClick('install_gateway', ctaLocation);
    }
    navigate(path);
    handleMobileMenuClose();
  };

  const goInstallGuide = () => {
    trackCtaClick('install_gateway', 'public_nav');
    navigate('/install-gateway');
  };

  const goRegister = () => {
    trackCtaClick('register', 'public_nav');
    navigate(registerHref);
  };

  return (
    <AppBar position="fixed" sx={{ bgcolor: 'background.paper', boxShadow: 1, zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <Box
            component="img"
            src="/eyesense-logo-v2.png"
            alt="EyeSense.ai Logo"
            sx={{ height: 54, width: 54, mr: 1.6, cursor: 'pointer' }}
            onClick={() => navigate('/')}
          />
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 'bold',
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              cursor: 'pointer',
            }}
            onClick={() => navigate('/')}
          >
            EyeSense.ai
          </Typography>
        </Box>
        {isAuthenticated ? (
          <>
            <Button
              color="primary"
              onClick={() => navigate('/dashboard')}
              sx={{ mr: 1 }}
            >
              Dashboard
            </Button>
            <Button
              color="primary"
              onClick={() => navigate('/gateways')}
              sx={{ mr: 1 }}
            >
              Gateways
            </Button>
            <Typography variant="body2" sx={{ mx: 2, color: 'text.secondary' }}>
              Welcome, {user?.username || user?.email}
            </Typography>
          </>
        ) : (
          <>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
              <Button
                color="primary"
                onClick={goInstallGuide}
                sx={{ mr: 1 }}
              >
                Install Gateway
              </Button>
              <Button
                color="primary"
                onClick={() => navigate('/subscription/plans')}
                sx={{ mr: 1 }}
              >
                Pricing
              </Button>
              <Button
                color="primary"
                startIcon={<LoginIcon />}
                onClick={() => navigate(loginHref)}
                sx={{ mr: 1 }}
              >
                Login
              </Button>
              <Button
                variant="contained"
                type="button"
                onClick={goRegister}
                sx={{ mr: 1 }}
              >
                Get Started
              </Button>
            </Box>
            <IconButton
              color="primary"
              onClick={handleMobileMenuOpen}
              sx={{ display: { xs: 'flex', md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={mobileMenuAnchor}
              open={Boolean(mobileMenuAnchor)}
              onClose={handleMobileMenuClose}
            >
              <MenuItem onClick={() => handleNavigation('/install-gateway', 'public_nav_mobile_install')}>
                Install Gateway
              </MenuItem>
              <MenuItem onClick={() => handleNavigation('/subscription/plans')}>
                Pricing
              </MenuItem>
              <MenuItem onClick={() => handleNavigation(loginHref)}>
                Login
              </MenuItem>
              <MenuItem onClick={() => handleNavigation(registerHref, 'public_nav_mobile_register')}>
                Get Started
              </MenuItem>
            </Menu>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default PublicNavBar;
